import type { UseFormReturn } from "react-hook-form";
import type { DemandFormData } from "./DemandIntakeForm";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { AlertCircle, Target } from "lucide-react";

interface StrategicAlignmentStepProps {
  form: UseFormReturn<DemandFormData>;
}

export function StrategicAlignmentStep({ form }: StrategicAlignmentStepProps) {
  const { register, formState: { errors }, setValue, watch } = form;

  return (
    <div className="space-y-6">
      {/* Strategic Objective */}
      <div className="space-y-2">
        <Label htmlFor="strategicObjective">
          Primary Strategic Objective <span className="text-destructive">*</span>
        </Label>
        <Select
          onValueChange={(value) => setValue("strategicObjective", value)}
          value={watch("strategicObjective")}
        >
          <SelectTrigger className={errors.strategicObjective ? "border-destructive" : ""}>
            <SelectValue placeholder="Select the primary strategic objective this supports" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="growth">Revenue Growth</SelectItem>
            <SelectItem value="efficiency">Operational Efficiency</SelectItem>
            <SelectItem value="innovation">Innovation & Technology</SelectItem>
            <SelectItem value="customer">Customer Experience</SelectItem>
            <SelectItem value="market">Market Expansion</SelectItem>
            <SelectItem value="compliance">Regulatory Compliance</SelectItem>
            <SelectItem value="sustainability">Sustainability</SelectItem>
            <SelectItem value="talent">Talent Development</SelectItem>
            <SelectItem value="risk">Risk Management</SelectItem>
            <SelectItem value="digital">Digital Transformation</SelectItem>
          </SelectContent>
        </Select>
        {errors.strategicObjective && (
          <div className="flex items-center gap-2 text-destructive text-sm">
            <AlertCircle className="h-4 w-4" />
            Strategic objective is required
          </div>
        )}
      </div>

      {/* Business Impact */}
      <div className="space-y-2">
        <Label htmlFor="businessImpact">
          Expected Business Impact <span className="text-destructive">*</span>
        </Label>
        <Textarea
          id="businessImpact"
          placeholder="Describe the specific business outcomes and measurable benefits this demand will deliver..."
          rows={4}
          {...register("businessImpact", {
            required: "Business impact description is required",
            minLength: {
              value: 30,
              message: "Business impact must be at least 30 characters"
            }
          })}
          className={errors.businessImpact ? "border-destructive" : ""}
        />
        {errors.businessImpact && (
          <div className="flex items-center gap-2 text-destructive text-sm">
            <AlertCircle className="h-4 w-4" />
            {errors.businessImpact.message}
          </div>
        )}
      </div>

      {/* Risk Assessment */}
      <div className="space-y-2">
        <Label htmlFor="riskAssessment">
          Risk Assessment <span className="text-destructive">*</span>
        </Label>
        <Textarea
          id="riskAssessment"
          placeholder="Identify potential risks and mitigation strategies. What could go wrong and how will you address it?"
          rows={4}
          {...register("riskAssessment", {
            required: "Risk assessment is required",
            minLength: {
              value: 25,
              message: "Risk assessment must be at least 25 characters"
            }
          })}
          className={errors.riskAssessment ? "border-destructive" : ""}
        />
        {errors.riskAssessment && (
          <div className="flex items-center gap-2 text-destructive text-sm">
            <AlertCircle className="h-4 w-4" />
            {errors.riskAssessment.message}
          </div>
        )}
      </div>

      {/* Alternatives Considered */}
      <div className="space-y-2">
        <Label htmlFor="alternatives">
          Alternatives Considered <span className="text-destructive">*</span>
        </Label>
        <Textarea
          id="alternatives"
          placeholder="What other solutions or approaches were considered? Why is this the preferred option?"
          rows={4}
          {...register("alternatives", {
            required: "Alternatives consideration is required",
            minLength: {
              value: 20,
              message: "Alternatives description must be at least 20 characters"
            }
          })}
          className={errors.alternatives ? "border-destructive" : ""}
        />
        {errors.alternatives && (
          <div className="flex items-center gap-2 text-destructive text-sm">
            <AlertCircle className="h-4 w-4" />
            {errors.alternatives.message}
          </div>
        )}
      </div>

      {/* Strategic Alignment Guidelines */}
      <div className="bg-primary/5 p-4 rounded-lg border border-primary/20">
        <div className="flex items-start gap-3">
          <Target className="h-5 w-5 text-primary mt-0.5" />
          <div>
            <h4 className="font-medium text-primary mb-2">Strategic Alignment Tips</h4>
            <ul className="text-sm text-primary/80 space-y-1">
              <li>• Link your request to specific company objectives or KPIs</li>
              <li>• Quantify expected outcomes wherever possible</li>
              <li>• Consider both short-term and long-term impacts</li>
              <li>• Address any potential conflicts with existing initiatives</li>
              <li>• Include timeline considerations for strategic planning cycles</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}